<template>
  <div>
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>拼团记录</span>
        <div class="header-actions">
          <el-button @click="$router.push('/group-buy')">返回活动列表</el-button>
          <el-button :loading="loading" @click="loadData">刷新</el-button>
        </div>
      </div>

      <el-alert
        v-if="query.activityId"
        class="state-block"
        type="info"
        :closable="false"
        show-icon
        title="当前正在查看某个团购活动的拼团记录"
      >
        <template slot>
          <span>活动编号：{{ query.activityId }}</span>
          <el-button type="text" @click="clearActivityFilter">清除筛选</el-button>
        </template>
      </el-alert>

      <el-form :inline="true" :model="query" class="query-form">
        <el-form-item>
          <el-input v-model.trim="query.activityId" placeholder="活动编号" clearable />
        </el-form-item>
        <el-form-item>
          <el-select v-model="query.status" placeholder="拼团状态" clearable>
            <el-option :value="0" label="拼团中" />
            <el-option :value="1" label="已成团" />
            <el-option :value="2" label="已关闭" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-select v-model="query.refundPending" placeholder="退款状态" clearable>
            <el-option :value="true" label="仅看待退款" />
            <el-option :value="false" label="排除待退款" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="resetQuery">重置</el-button>
          <el-button type="primary" @click="loadData">查询</el-button>
        </el-form-item>
      </el-form>

      <el-alert
        v-if="loadError"
        class="state-block"
        title="拼团记录加载失败"
        type="error"
        :closable="false"
        show-icon
      >
        <template slot>
          <span>可以稍后重试，或先检查后端服务状态。</span>
          <el-button type="text" @click="loadData">重新加载</el-button>
        </template>
      </el-alert>

      <template v-else>
        <el-empty v-if="!loading && !list.length" description="当前没有拼团记录" />

        <template v-else>
          <el-table :data="list" border v-loading="loading">
            <el-table-column prop="id" label="拼团编号" width="120" />
            <el-table-column label="活动 / 商品" min-width="260">
              <template slot-scope="{ row }">
                <div class="title-cell">
                  <div class="title-cell__title">{{ row.activityTitle || `活动 #${row.activityId}` }}</div>
                  <div class="title-cell__meta">{{ row.productName || `商品 #${row.productId}` }}</div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="团长" width="160">
              <template slot-scope="{ row }">
                <div>{{ row.leaderNickname || `用户 #${row.leaderUserId}` }}</div>
              </template>
            </el-table-column>
            <el-table-column label="规格" width="150">
              <template slot-scope="{ row }">
                <div>{{ row.skuName || `SKU #${row.skuId}` }}</div>
              </template>
            </el-table-column>
            <el-table-column label="拼团状态" width="120">
              <template slot-scope="{ row }">
                <el-tag :type="statusType(row.status)">{{ statusText(row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="参团进度" width="180">
              <template slot-scope="{ row }">
                <div>{{ row.joinedPeople || 0 }} / {{ row.requiredPeople || 0 }}</div>
                <div class="sub-text">已支付 {{ row.paidMemberCount || 0 }}</div>
              </template>
            </el-table-column>
            <el-table-column label="成团方式" width="170">
              <template slot-scope="{ row }">
                <div>{{ successSourceText(row.successSource) }}</div>
                <div v-if="row.successRemark" class="sub-text">{{ row.successRemark }}</div>
              </template>
            </el-table-column>
            <el-table-column label="APP展示" width="130">
              <template slot-scope="{ row }">
                <el-tag v-if="Number(row.appDisplay) === 1" type="success">继续展示</el-tag>
                <span v-else class="sub-text">不展示</span>
              </template>
            </el-table-column>
            <el-table-column label="退款状态" width="180">
              <template slot-scope="{ row }">
                <el-tag v-if="row.refundRecordCount" :type="refundStatusType(row.latestRefundStatus)">
                  {{ refundStatusText(row) }}
                </el-tag>
                <span v-else class="sub-text">-</span>
                <div v-if="row.refundRecordCount" class="sub-text">
                  记录 {{ row.refundRecordCount }} 条
                  <span v-if="row.latestRefundNo"> / {{ row.latestRefundNo }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="时间信息" min-width="220">
              <template slot-scope="{ row }">
                <div>截止：{{ formatDateTime(row.expireTime) }}</div>
                <div class="sub-text">成团：{{ formatDateTime(row.successTime) }}</div>
              </template>
            </el-table-column>
            <el-table-column label="处理结果" min-width="220">
              <template slot-scope="{ row }">
                <div>{{ handleResultText(row) }}</div>
                <div v-if="handleTimeText(row)" class="sub-text">处理时间：{{ handleTimeText(row) }}</div>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="460">
              <template slot-scope="{ row }">
                <el-button type="text" @click="goOrders(row.activityId)">关联订单</el-button>
                <el-button v-if="row.refundRecordCount" type="text" @click="goRefunds(row.id)">查看退款</el-button>
                <el-button
                  v-if="canComplete(row)"
                  type="text"
                  :loading="actionId === `${row.id}-complete`"
                  @click="completeRecord(row)"
                >
                  提前成团
                </el-button>
                <el-button
                  v-if="canFillSuccess(row)"
                  type="text"
                  :loading="actionId === `${row.id}-fill`"
                  @click="fillSuccess(row)"
                >
                  补满人数并成团
                </el-button>
                <el-button
                  v-if="canToggleAppDisplay(row)"
                  type="text"
                  :loading="actionId === `${row.id}-display`"
                  @click="toggleAppDisplay(row)"
                >
                  {{ Number(row.appDisplay) === 1 ? '取消APP展示' : '继续在APP展示' }}
                </el-button>
                <el-button
                  v-if="row.status === 0 && Number(row.paidMemberCount || 0) === 0"
                  type="text"
                  :loading="actionId === `${row.id}-close`"
                  @click="closeRecord(row)"
                >
                  关闭记录
                </el-button>
                <el-button
                  v-if="canMarkFailed(row)"
                  type="text"
                  :loading="actionId === `${row.id}-fail`"
                  @click="markFailed(row)"
                >
                  标记失败待退款
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="table-footer">
            <div class="table-footer__meta">共 {{ total }} 条</div>
            <el-pagination
              background
              layout="prev, pager, next, sizes"
              :current-page="query.pageNum"
              :page-sizes="[10, 20, 50]"
              :page-size="query.pageSize"
              :total="total"
              @current-change="changePage"
              @size-change="changePageSize"
            />
          </div>
        </template>
      </template>
    </el-card>
  </div>
</template>

<script>
import {
  closeGroupBuyRecord,
  completeGroupBuyRecord,
  failGroupBuyRecord,
  fillSuccessGroupBuyRecord,
  getGroupBuyRecordPage,
  updateGroupBuyRecordAppDisplay
} from '../../api/admin'
import { formatDateTime } from '../../utils/format'

export default {
  data() {
    return {
      query: {
        activityId: '',
        status: '',
        refundPending: '',
        pageNum: 1,
        pageSize: 10
      },
      list: [],
      total: 0,
      loading: false,
      loadError: false,
      actionId: null
    }
  },
  created() {
    this.restoreQuery()
    this.loadData()
  },
  methods: {
    formatDateTime,
    statusText(status) {
      const map = { 0: '拼团中', 1: '已成团', 2: '已关闭' }
      return map[status] || '未知状态'
    },
    statusType(status) {
      const map = { 0: 'warning', 1: 'success', 2: 'info' }
      return map[status] || 'info'
    },
    successSourceText(source) {
      const map = {
        NATURAL: '自然成团',
        ADMIN_COMPLETE: '后台提前成团',
        ADMIN_FILL: '后台补满成团'
      }
      return map[source] || '-'
    },
    refundStatusText(row) {
      if (row.refundPending) {
        return '退款处理中'
      }
      const map = {
        2: '退款成功',
        3: '退款失败'
      }
      return map[Number(row.latestRefundStatus)] || '已生成退款单'
    },
    refundStatusType(status) {
      const map = {
        0: 'warning',
        1: 'warning',
        2: 'success',
        3: 'danger'
      }
      return map[Number(status)] || 'info'
    },
    handleResultText(row) {
      if (Number(row.status) === 0) {
        return '等待成团中'
      }
      if (Number(row.status) === 1) {
        return '已成团'
      }
      if (!row.refundRecordCount) {
        return Number(row.paidMemberCount || 0) > 0 ? '已关闭，待退款处理' : '已关闭，无需退款'
      }
      const map = {
        0: '已自动生成退款单',
        1: '退款处理中',
        2: '退款成功',
        3: '退款失败，待人工处理'
      }
      return map[Number(row.latestRefundStatus)] || '已关闭'
    },
    handleTimeText(row) {
      return formatDateTime(row.latestRefundCallbackTime || row.updateTime)
    },
    restoreQuery() {
      const routeQuery = this.$route.query.q
      if (!routeQuery) return
      try {
        const parsed = JSON.parse(routeQuery)
        this.query = { ...this.query, ...parsed }
      } catch (error) {
        // ignore invalid route query
      }
    },
    async loadData() {
      this.loading = true
      this.loadError = false
      try {
        const data = await getGroupBuyRecordPage(this.query)
        this.list = data.list || []
        this.total = data.total || 0
      } catch (error) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    resetQuery() {
      this.query = {
        activityId: '',
        status: '',
        refundPending: '',
        pageNum: 1,
        pageSize: 10
      }
      this.loadData()
    },
    clearActivityFilter() {
      this.query.activityId = ''
      this.query.pageNum = 1
      this.loadData()
    },
    changePage(page) {
      this.query.pageNum = page
      this.loadData()
    },
    changePageSize(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    canComplete(row) {
      return Number(row.status) === 0 && Number(row.paidMemberCount || 0) > 0
    },
    canFillSuccess(row) {
      return Number(row.paidMemberCount || 0) > 0 && Number(row.joinedPeople || 0) < Number(row.requiredPeople || 0) && Number(row.status) !== 2
    },
    canToggleAppDisplay(row) {
      return Number(row.status) === 1
    },
    canMarkFailed(row) {
      if (Number(row.status) !== 0 || Number(row.paidMemberCount || 0) <= 0) return false
      if (!row.expireTime) return false
      const expire = new Date(row.expireTime).getTime()
      return !Number.isNaN(expire) && expire < Date.now()
    },
    goOrders(activityId) {
      this.$router.push({
        path: '/orders',
        query: {
          q: JSON.stringify({
            orderNo: '',
            receiverMobile: '',
            orderType: 1,
            orderStatus: '',
            groupBuyActivityId: activityId,
            pageNum: 1,
            pageSize: 20
          })
        }
      })
    },
    goRefunds(recordId) {
      this.$router.push({
        path: '/group-buy/refunds',
        query: {
          q: JSON.stringify({
            groupBuyRecordId: recordId,
            orderNo: '',
            refundStatus: '',
            pageNum: 1,
            pageSize: 10
          })
        }
      })
    },
    async runRowAction(actionKey, rowId, handler) {
      this.actionId = `${rowId}-${actionKey}`
      try {
        await handler()
        await this.loadData()
      } finally {
        this.actionId = null
      }
    },
    completeRecord(row) {
      this.$confirm(`确认将拼团记录 #${row.id} 提前成团吗？系统会立即把该团标记为成功。`, '提前成团', {
        type: 'warning'
      }).then(() => this.runRowAction('complete', row.id, async () => {
        await completeGroupBuyRecord(row.id)
        this.$message.success('拼团记录已提前成团')
      })).catch(() => {})
    },
    fillSuccess(row) {
      this.$confirm(`确认将拼团记录 #${row.id} 补满人数并成团吗？系统会把参团人数补到 ${row.requiredPeople} 人。`, '补满人数并成团', {
        type: 'warning'
      }).then(() => this.runRowAction('fill', row.id, async () => {
        await fillSuccessGroupBuyRecord(row.id)
        this.$message.success('拼团记录已补满人数并成团')
      })).catch(() => {})
    },
    toggleAppDisplay(row) {
      const nextValue = Number(row.appDisplay) === 1 ? 0 : 1
      const actionText = nextValue === 1 ? '继续在APP展示' : '取消APP展示'
      this.$confirm(`确认将拼团记录 #${row.id} 设置为“${actionText}”吗？`, 'APP展示设置', {
        type: 'warning'
      }).then(() => this.runRowAction('display', row.id, async () => {
        await updateGroupBuyRecordAppDisplay(row.id, nextValue)
        this.$message.success(`已${actionText}`)
      })).catch(() => {})
    },
    closeRecord(row) {
      this.$confirm(`确认关闭拼团记录 #${row.id} 吗？`, '关闭记录', {
        type: 'warning'
      }).then(() => this.runRowAction('close', row.id, async () => {
        await closeGroupBuyRecord(row.id)
        this.$message.success('拼团记录已关闭')
      })).catch(() => {})
    },
    markFailed(row) {
      this.$confirm(`确认将拼团记录 #${row.id} 标记为失败待退款吗？`, '标记失败', {
        type: 'warning'
      }).then(() => this.runRowAction('fail', row.id, async () => {
        await failGroupBuyRecord(row.id)
        this.$message.success('已标记为失败待退款')
      })).catch(() => {})
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.query-form {
  margin-bottom: 12px;
}

.state-block {
  margin-bottom: 16px;
}

.title-cell__title {
  font-weight: 600;
}

.title-cell__meta,
.sub-text {
  margin-top: 4px;
  color: #6b7280;
  font-size: 13px;
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
}

.table-footer__meta {
  color: #6b7280;
  font-size: 13px;
}
</style>
